import BigNumber from 'bignumber.js'
import React from 'react'
import ReactDOMServer from 'react-dom/server'
import styled from 'styled-components'
import { GATSBY_MAIN_ACHIVMENTS_URL, GATSBY_MAIN_URL } from '../../../../constants/env'
import { useCollateralById } from '../../../../hooks/useCollateralById'
import useUsdValue from '../../../../hooks/useUsdValue'
import { coinIcons } from '../../../../templates/CopytradingTemplate/utils/coinIcons'
import { DropdownCopytradingTopTradersItemProps } from './types'

const ACHIEVEMENT_WIDTH_TOOLTIP = 136
const ACHIEVEMENT_HEIGHT_TOOLTIP = 136
const ACHIEVEMENT_WIDTH = 16
const ACHIEVEMENT_HEIGHT = 16
const COLLATERAL_WIDTH = 24
const COLLATERAL_HEIGHT = 24

export const DropdownCopytradingTopTradersItem = (props: DropdownCopytradingTopTradersItemProps) => {
  const { account, backgroundUrl } = props
  const { name, achievements = [], profitability, equity, collateralId, investorsAmount, id } = account || {}
  const collateral = useCollateralById(collateralId)
  const { usdFormatted: equityUsd } = useUsdValue({
    value: +equity,
    collateralId,
    withPlus: true,
    withAbout: false,
    simplifyFormat: true,
  })

  return (
    <StyledDropdownCopytradingTopTradersItem $backgroundUrl={backgroundUrl}>
      <Header>
        <HeaderNameContainer>
          <HedaerName>{name}</HedaerName>
          <HeaderAchievementsList>
            {achievements.map(achievement => (
              <HeaderAchievementsListItem
                key={achievement.name}
                data-tooltip-id='copytrading-header-tooltip'
                data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
                  <TooltipConteiner>
                    <svg width={ACHIEVEMENT_WIDTH_TOOLTIP} height={ACHIEVEMENT_HEIGHT_TOOLTIP}>
                      <image
                        href={`${GATSBY_MAIN_ACHIVMENTS_URL}${achievement.name}_TOOLTIP.svg`}
                        width={ACHIEVEMENT_WIDTH_TOOLTIP}
                        height={ACHIEVEMENT_HEIGHT_TOOLTIP}
                      />
                    </svg>
                    <TooltipText>{achievement.description}</TooltipText>
                  </TooltipConteiner>
                )}>
                <svg width={ACHIEVEMENT_WIDTH} height={ACHIEVEMENT_HEIGHT}>
                  <image
                    href={`${GATSBY_MAIN_ACHIVMENTS_URL}${achievement.name}.svg`}
                    width={ACHIEVEMENT_WIDTH}
                    height={ACHIEVEMENT_HEIGHT}
                  />
                </svg>
              </HeaderAchievementsListItem>
            ))}
          </HeaderAchievementsList>
        </HeaderNameContainer>
        <HeaderFollowButton rel='noopener noreferrer' href={`${GATSBY_MAIN_URL}/app/copytrading/${id}/about`}>
          follow
        </HeaderFollowButton>
      </Header>
      <Footer>
        <FooterRoeContainer>
          <FooterRoeValue>{new BigNumber(profitability).dp(0).toFormat()}%</FooterRoeValue>
          <FooterRoeLabel>{'ROE (All)'}</FooterRoeLabel>
        </FooterRoeContainer>
        <FooterItemContainer $minWidth={81} $minWidthMobile={71}>
          <FooterItemValue $isPositive={new BigNumber(equity).isPositive()}>{equityUsd}</FooterItemValue>
          <FooterItemLabel>Strategy Equity</FooterItemLabel>
        </FooterItemContainer>
        <FooterItemContainer $minWidth={43} $minWidthMobile={42}>
          <FooterItemValue>{new BigNumber(investorsAmount).toFormat()}</FooterItemValue>
          <FooterRoeLabel>Followers</FooterRoeLabel>
        </FooterItemContainer>
        <FooterCollateralContainer>
          <svg width={COLLATERAL_WIDTH} height={COLLATERAL_HEIGHT}>
            <image width={COLLATERAL_WIDTH} height={COLLATERAL_HEIGHT} href={coinIcons[collateral?.asset || 0]?.[0]} />
          </svg>
        </FooterCollateralContainer>
      </Footer>
    </StyledDropdownCopytradingTopTradersItem>
  )
}

const StyledDropdownCopytradingTopTradersItem = styled.div<{ $backgroundUrl: string }>`
  box-shadow: 0px 4px 8px rgba(24, 98, 196, 0.2);
  border-radius: 8px;
  flex-grow: 1;
  background: #123482;
  padding: 17px 12px 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-image: url(${({ $backgroundUrl }) => $backgroundUrl});
  max-height: 153px;

  @media ${({ theme }) => theme.device.xxmMax} {
    max-width: 241px;
    height: 153px;
    width: 100%;
  }
`
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const HeaderNameContainer = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const HedaerName = styled.span`
  font-family: 'Gilroy';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;

  @media ${({ theme }) => theme.device.xxmMax} {
    font-size: 14px;
    line-height: 17px;
  }
`

const HeaderAchievementsList = styled.ul`
  display: flex;
  gap: 4px;
`

const HeaderAchievementsListItem = styled.li`
  display: flex;
  height: fit-content;
  cursor: help;
`

const HeaderFollowButton = styled.a`
  background-color: transparent;
  outline: none;
  box-shadow: none;
  border: none;
  cursor: pointer;
  font-family: 'Gilroy';
  font-style: normal;
  font-weight: 800;
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #04524b;
  background: linear-gradient(180deg, #a4fff6 0%, #1ddccd 100%);
  box-shadow: 0px 6px 13px 2px rgba(23, 255, 234, 0.24);
  border-radius: 4px;
  padding: 4px 10px;

  @media ${({ theme }) => theme.device.xxmMax} {
    font-size: 10px;
    line-height: 12px;
  }
`

const Footer = styled.div`
  display: flex;
  gap: 15px;

  @media ${({ theme }) => theme.device.xxmMax} {
    gap: 6px;
  }
`

const FooterRoeContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 51px;

  @media ${({ theme }) => theme.device.xxmMax} {
    min-width: 50px;
  }
`

const FooterRoeValue = styled.span`
  font-family: 'Gilroy';
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 25px;
  color: #0bca6f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${({ theme }) => theme.device.xxmMax} {
    font-size: 14px;
    line-height: 17px;
  }
`

const FooterRoeLabel = styled.span`
  font-family: 'Gilroy';
  font-style: normal;
  font-weight: 600;
  font-size: 10px;
  line-height: 14px;
  color: #ffffff;
  opacity: 0.7;

  @media ${({ theme }) => theme.device.xxmMax} {
    font-size: 10px;
    line-height: 14px;
  }
`

const FooterItemContainer = styled.div<{ $minWidth: number; $minWidthMobile: number }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: flex-end;
  min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : 'auto')};

  @media ${({ theme }) => theme.device.xxmMax} {
    min-width: ${({ $minWidthMobile }) => ($minWidthMobile ? `${$minWidthMobile}px` : 'auto')};
  }
`

const FooterItemValue = styled.span<{ $isPositive?: boolean }>`
  font-family: 'Gilroy';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 18px;
  align-items: center;
  color: ${({ $isPositive }) => ($isPositive ? '#0bca6f' : '#ffffff')};
  padding-top: 5px;

  height: 25px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${({ theme }) => theme.device.xxmMax} {
    height: 17px;
    font-size: 14px;
    line-height: 17px;
    padding-top: 0;
  }
`

const FooterItemLabel = styled.span`
  font-family: 'Gilroy';
  font-style: normal;
  font-weight: 600;
  font-size: 10px;
  line-height: 14px;
  color: #ffffff;
  opacity: 0.7;

  @media ${({ theme }) => theme.device.xxmMax} {
    font-size: 10px;
    line-height: 14px;
  }
`

const FooterCollateralContainer = styled.div`
  margin: auto;
`

const TooltipConteiner = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 139px;
  justify-content: center;
  align-items: center;
`

const TooltipText = styled.span`
  font-family: 'Gilroy';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  color: #324875;
  opacity: 0.8;
`
